import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export function useListSearchParams<D extends Record<string, string | number>>(
  defaults: D,
) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [stableDefaults] = useState(() => defaults)

  const page = useMemo(() => {
    const raw = searchParams.get('page')
    if (raw === null) return 1
    const parsed = parseInt(raw, 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
  }, [searchParams])

  const filters = useMemo(() => {
    const defs = stableDefaults
    const result = {} as D
    for (const key in defs) {
      const raw = searchParams.get(key)
      if (raw === null) {
        result[key] = defs[key]
      } else if (typeof defs[key] === 'number') {
        const parsed = Number(raw)
        result[key] = (Number.isFinite(parsed) ? parsed : defs[key]) as D[typeof key]
      } else {
        result[key] = raw as D[typeof key]
      }
    }
    return result
  }, [searchParams, stableDefaults])

  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (newPage <= 1) {
            next.delete('page')
          } else {
            next.set('page', String(newPage))
          }
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const setFilter = useCallback(
    <K extends keyof D>(key: K, value: D[K]) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('page')
          const defs = stableDefaults
          if (value === defs[key]) {
            next.delete(key as string)
          } else {
            next.set(key as string, String(value))
          }
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams, stableDefaults],
  )

  return { page, filters, setPage, setFilter }
}
